import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useUser } from "@/store/user";

function ChallengeRunner({ challenge, onClose, onSolved }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.submitChallenge(challenge.id, text);
      setResult(res);
      onSolved && onSolved(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذّر التقييم. حاول مجدّدًا.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="completion"
      onClick={onClose}
      data-testid="challenge-runner"
      style={{ alignItems: "flex-start", overflowY: "auto" }}
    >
      <div
        className="completion-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, textAlign: "right", marginTop: 60 }}
      >
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <div
              className="ic"
              style={{
                width: 44, height: 44, borderRadius: 14,
                display: "grid", placeItems: "center",
                background: "rgba(245,200,122,0.12)",
                border: "1px solid rgba(245,200,122,0.28)",
                fontSize: 22,
              }}
            >
              {challenge.icon}
            </div>
            <div>
              <h2 style={{ fontSize: 22, margin: 0, fontFamily: "'Reem Kufi', sans-serif" }}>
                {challenge.title}
              </h2>
              <div className="muted" style={{ fontSize: 12 }}>
                {challenge.difficulty} · {challenge.duration} · +{challenge.xp} XP
              </div>
            </div>
          </div>
          <button
            className="btn btn-outline"
            onClick={onClose}
            data-testid="challenge-close-btn"
            style={{ padding: "8px 12px", fontSize: 13 }}
          >
            ✕
          </button>
        </div>

        <p style={{ lineHeight: 1.9, color: "var(--text-dim)", textAlign: "right" }}>
          {challenge.prompt}
        </p>

        {!result ? (
          <>
            <textarea
              className="balagha-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="اكتب إجابتك هنا..."
              data-testid="challenge-input"
            />
            {error && <div className="hint" style={{ color: "#ff7aa8" }}>{error}</div>}
            <div className="row" style={{ marginTop: 10, justifyContent: "flex-start" }}>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={loading || !text.trim()}
                data-testid="challenge-submit-btn"
              >
                {loading ? "يقيّم..." : "أرسل وقيّم ✨"}
              </button>
            </div>
          </>
        ) : (
          <div className="eval-card" data-testid="challenge-result">
            <div className="eval-score">
              <div className="ring" style={{ "--p": result.overall_score }}>
                <span>{result.overall_score}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>تقييم المعلّم</div>
                <div className="feedback-block">{result.feedback}</div>
              </div>
            </div>
            {result.improvement && (
              <div style={{ marginTop: 6 }}>
                <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>اقتراح تحسين</div>
                <div className="feedback-block">{result.improvement}</div>
              </div>
            )}
            {result.rewritten && (
              <div className="improve-out">
                ✨ صياغة بلاغية مقترحة:
                <div style={{ marginTop: 6 }}>«{result.rewritten}»</div>
              </div>
            )}
            <div className="row" style={{ marginTop: 12 }}>
              <span className="badge-pill">+{result.xp_earned} XP</span>
              {result.first_time && <span className="badge-pill">تحدّي جديد ✓</span>}
            </div>
            <div className="row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={onClose}>إنهاء</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChallengesPage() {
  const { refresh } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    api.listChallenges()
      .then(setItems)
      .catch((e) => setError(e?.message || "تعذّر تحميل التحدّيات"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const daily = items.filter((c) => c.cadence === "daily");
  const weekly = items.filter((c) => c.cadence === "weekly");

  return (
    <div className="shell has-bottom-nav" data-testid="challenges-page">
      <div className="page-header">
        <h1>
          <span className="gold">التحدّيات</span> اليومية والأسبوعيّة
        </h1>
        <p>تمارين قصيرة لشحذ مهاراتك البلاغيّة، مع تقييم AI فوريّ ومكافآت XP.</p>
      </div>

      {loading ? (
        <div className="muted" data-testid="challenges-loading">يُحمَّل...</div>
      ) : error ? (
        <div
          data-testid="challenges-error"
          style={{
            padding: 20, marginTop: 20,
            background: "rgba(255,122,168,0.08)",
            border: "1px solid rgba(255,122,168,0.3)",
            borderRadius: 12, textAlign: "center",
          }}
        >
          <p style={{ color: "#ff7aa8", marginBottom: 12 }}>
            ⚠️ تعذّر تحميل التحدّيات: {error}
          </p>
          <button className="btn btn-primary" onClick={reload} data-testid="challenges-retry">
            إعادة المحاولة
          </button>
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="challenges-empty"
          className="muted"
          style={{ textAlign: "center", padding: 40 }}
        >
          لا توجد تحدّيات متاحة حاليًّا. تحقّق لاحقًا من فضلك.
        </div>
      ) : (
        <>
          <h2 className="section-title">🌞 تحدّيات اليوم</h2>
          <div className="challenges-grid" style={{ marginBottom: 30 }}>
            {daily.map((c) => (
              <div
                key={c.id}
                className="challenge-card"
                onClick={() => setActive(c)}
                data-testid={`challenge-card-${c.id}`}
              >
                <div className="row-top">
                  <div className="ic daily">{c.icon}</div>
                  <span className={`tag ${c.completed ? "done" : ""}`}>
                    {c.completed ? "✓ مكتمل" : c.difficulty}
                  </span>
                </div>
                <h3>{c.title}</h3>
                <div className="desc">{c.prompt}</div>
                <div className="footer">
                  <span className="xp">+{c.xp} XP</span>
                  <span className="muted" style={{ fontSize: 13 }}>⏱ {c.duration}</span>
                </div>
              </div>
            ))}
          </div>

          <h2 className="section-title">🏆 تحدّي الأسبوع</h2>
          <div className="challenges-grid">
            {weekly.map((c) => (
              <div
                key={c.id}
                className="challenge-card"
                onClick={() => setActive(c)}
                data-testid={`challenge-card-${c.id}`}
              >
                <div className="row-top">
                  <div className="ic">{c.icon}</div>
                  <span className={`tag ${c.completed ? "done" : ""}`}>
                    {c.completed ? "✓ مكتمل" : c.difficulty}
                  </span>
                </div>
                <h3>{c.title}</h3>
                <div className="desc">{c.prompt}</div>
                <div className="footer">
                  <span className="xp">+{c.xp} XP</span>
                  <span className="muted" style={{ fontSize: 13 }}>⏱ {c.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {active && (
        <ChallengeRunner
          challenge={active}
          onClose={() => {
            setActive(null);
            reload();
            refresh();
          }}
          onSolved={() => {}}
        />
      )}
    </div>
  );
}
